// Internal pending-action store. 1 action at a time — new action supersedes prior
// (prior bị reject). Solid signal-backed.

import { createSignal, type Accessor } from "solid-js";
import type { PendingAskUser, PendingUiAction } from "./types";

export interface PendingUiActionStore {
  current: Accessor<PendingUiAction | null>;
  /// Set new pending. Nếu đã có prior → reject prior trước.
  replace: (next: PendingUiAction) => void;
  /// Resolve current + clear. No-op nếu empty.
  resolve: (result: unknown) => void;
  /// Reject current + clear. No-op nếu empty.
  reject: (reason: string) => void;
}

export function createPendingUiActionStore(): PendingUiActionStore {
  const [current, setCurrent] = createSignal<PendingUiAction | null>(null);

  return {
    current,
    replace(next) {
      const prior = current();
      if (prior) {
        try {
          prior.reject("superseded by new tool call");
        } catch {
          // Swallow — defensive against double-reject.
        }
      }
      setCurrent(next);
    },
    resolve(result) {
      const action = current();
      if (!action) return;
      setCurrent(null);
      try {
        action.resolve(result);
      } catch {
        // Swallow — Promise có thể đã settle.
      }
    },
    reject(reason) {
      const action = current();
      if (!action) return;
      setCurrent(null);
      try {
        action.reject(reason);
      } catch {
        // Swallow.
      }
    },
  };
}

// ─── PendingAskUser store (parallel pattern cho clarification questions) ───

export interface PendingAskUserStore {
  current: Accessor<PendingAskUser | null>;
  replace: (next: PendingAskUser) => void;
  resolve: (answer: string) => void;
  reject: (reason: string) => void;
}

export function createPendingAskUserStore(): PendingAskUserStore {
  const [current, setCurrent] = createSignal<PendingAskUser | null>(null);

  return {
    current,
    replace(next) {
      const prior = current();
      if (prior) {
        try {
          prior.reject("superseded by new question");
        } catch {
          // Swallow.
        }
      }
      setCurrent(next);
    },
    resolve(answer) {
      const q = current();
      if (!q) return;
      setCurrent(null);
      try {
        q.resolve(answer);
      } catch {
        // Swallow.
      }
    },
    reject(reason) {
      const q = current();
      if (!q) return;
      setCurrent(null);
      try {
        q.reject(reason);
      } catch {
        // Swallow.
      }
    },
  };
}
